import { useMemo, useState, type FunctionComponent } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Popconfirm, Table, Tag, Typography, message } from "antd";

import { ChallengeParticipantStatus, type ChallengeStatus } from "@fitness-app/data-models/entities/Challenge";

import { challengeStatusColors, challengeStatusLabels } from "~/modules/Challenges/constants";
import { formatChallengeDateRange } from "~/modules/Challenges/helpers";
import { supabase } from "~/store/initializeStore";
import { challengesHabitsKeys } from "./queryKeys";
import AddToChallengeModal from "./AddToChallengeModal";

type ParticipantRow = {
  id: string;
  challengeId: string;
  status: string;
  challenge: {
    name: string;
    status: ChallengeStatus;
    startDate: string | null;
    endDate: string | null;
  } | null;
};

type SnapshotRow = {
  challengeId: string;
  points: number;
  rank: number | null;
  completedTasks: number;
  totalTasks: number;
};

const participantStatusLabels: Record<string, string> = {
  [ChallengeParticipantStatus.Active]: "Aktywny",
  [ChallengeParticipantStatus.Pending]: "Oczekuje",
  [ChallengeParticipantStatus.Left]: "Opuścił",
  [ChallengeParticipantStatus.Completed]: "Ukończył",
};

const participantStatusColors: Record<string, string> = {
  [ChallengeParticipantStatus.Active]: "green",
  [ChallengeParticipantStatus.Pending]: "gold",
  [ChallengeParticipantStatus.Left]: "red",
  [ChallengeParticipantStatus.Completed]: "blue",
};

type Props = {
  traineeId: string;
  userId: string | null;
};

const TraineeChallengesSection: FunctionComponent<Props> = ({ traineeId, userId }) => {
  const queryClient = useQueryClient();
  const [isAddOpen, setIsAddOpen] = useState(false);

  const participantsQuery = useQuery({
    queryKey: challengesHabitsKeys.challengeParticipants(traineeId),
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_participant")
        .select("id, challengeId, status, challenge:challengeId(name, status, startDate, endDate)")
        .eq("traineeId", traineeId)
        .order("joinedAt", { ascending: false })
        .returns<ParticipantRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const snapshotsQuery = useQuery({
    queryKey: challengesHabitsKeys.challengeSnapshots(traineeId),
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_score_snapshot")
        .select("challengeId, points, rank, completedTasks, totalTasks")
        .eq("traineeId", traineeId)
        .returns<SnapshotRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const snapshotByChallenge = useMemo(() => {
    const map = new Map<string, SnapshotRow>();
    for (const snapshot of snapshotsQuery.data ?? []) {
      map.set(snapshot.challengeId, snapshot);
    }
    return map;
  }, [snapshotsQuery.data]);

  const joinedChallengeIds = useMemo(
    () =>
      (participantsQuery.data ?? [])
        .filter((row) => row.status !== ChallengeParticipantStatus.Left)
        .map((row) => row.challengeId),
    [participantsQuery.data],
  );

  const leaveMutation = useMutation({
    mutationFn: async (participantId: string) => {
      const { error } = await supabase
        .from("challenge_participant")
        .update({ status: ChallengeParticipantStatus.Left } as never)
        .eq("id", participantId);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Klient usunięty z wyzwania");
      void queryClient.invalidateQueries({ queryKey: challengesHabitsKeys.challengeParticipants(traineeId) });
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się usunąć z wyzwania: ${error.message}`);
    },
  });

  return (
    <Card
      title="Wyzwania"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddOpen(true)}>
          Dodaj do wyzwania
        </Button>
      }
    >
      <Table<ParticipantRow>
        dataSource={participantsQuery.data ?? []}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={participantsQuery.isFetching}
        locale={{ emptyText: "Klient nie bierze udziału w żadnym wyzwaniu" }}
        pagination={false}
      >
        <Table.Column<ParticipantRow>
          title="Wyzwanie"
          key="name"
          render={(_, row) => <Typography.Text strong>{row.challenge?.name ?? "-"}</Typography.Text>}
        />
        <Table.Column<ParticipantRow>
          title="Status wyzwania"
          key="challengeStatus"
          render={(_, row) =>
            row.challenge?.status ? (
              <Tag color={challengeStatusColors[row.challenge.status] ?? "default"}>
                {challengeStatusLabels[row.challenge.status] ?? row.challenge.status}
              </Tag>
            ) : (
              "-"
            )
          }
        />
        <Table.Column<ParticipantRow>
          title="Udział"
          dataIndex="status"
          key="participantStatus"
          render={(status: string) => (
            <Tag color={participantStatusColors[status] ?? "default"}>{participantStatusLabels[status] ?? status}</Tag>
          )}
        />
        <Table.Column<ParticipantRow>
          title="Okres"
          key="dateRange"
          render={(_, row) => formatChallengeDateRange(row.challenge?.startDate, row.challenge?.endDate)}
        />
        <Table.Column<ParticipantRow>
          title="Punkty"
          key="points"
          render={(_, row) => snapshotByChallenge.get(row.challengeId)?.points ?? 0}
        />
        <Table.Column<ParticipantRow>
          title="Pozycja"
          key="rank"
          render={(_, row) => {
            const rank = snapshotByChallenge.get(row.challengeId)?.rank;
            return rank ? `#${rank}` : "-";
          }}
        />
        <Table.Column<ParticipantRow>
          title="Postęp"
          key="progress"
          render={(_, row) => {
            const snapshot = snapshotByChallenge.get(row.challengeId);
            return snapshot ? `${snapshot.completedTasks}/${snapshot.totalTasks}` : "-";
          }}
        />
        <Table.Column<ParticipantRow>
          title="Opcje"
          key="action"
          render={(_, row) =>
            row.status === ChallengeParticipantStatus.Left ? null : (
              <Popconfirm
                title="Usunąć klienta z wyzwania?"
                okText="Usuń"
                cancelText="Anuluj"
                okButtonProps={{ danger: true, loading: leaveMutation.isLoading }}
                onConfirm={() => leaveMutation.mutate(row.id)}
              >
                <Button type="link" danger className="px-0">
                  Usuń z wyzwania
                </Button>
              </Popconfirm>
            )
          }
        />
      </Table>

      <AddToChallengeModal
        open={isAddOpen}
        traineeId={traineeId}
        userId={userId}
        joinedChallengeIds={joinedChallengeIds}
        onClose={() => setIsAddOpen(false)}
      />
    </Card>
  );
};

export default TraineeChallengesSection;
